import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { useIsObserver } from '@platform/features/auth';
import { isFieldRestricted } from '../../fields/fieldRestricted';
import { CountPill } from './CountPill';
import { FavouriteToggle } from './FavouriteToggle';
import type { IRecordToolbarProps } from './IRecordToolbarProps';
import { RecordIcon } from './RecordIcon';
import { ToolIconButton } from './ToolIconButton';

/**
 * Панель дій запису (UIUX forms/toolbar, прототип v4 «Record actions»; вигляд панелі таблиці): «Редагувати» або «Зберегти · N» +
 * «Скасувати» · «Видалити» | «Обране» · Призначити мені · Дублювати · Копіювати посилання · Друк · Експорт · [зв'язки | навігатор | +]
 * · [коментарі | +] · [файли | +] · «Поради» · «Зіставлення» · «Запитати ШІ». Новий запис (ще не збережений): ті самі кнопки, але дії,
 * яким потрібен збережений запис, вимкнені з підказкою «Спершу збережіть запис». Дії зі зміною даних — за дозволами запису від сервера;
 * особисті («Обране», «Запитати ШІ») — не в сесії спостерігача (лише перегляд). «Дублювати» вимкнено, коли обов'язкове поле приховане від
 * користувача (персональні дані): копія без нього не збереглася б.
 */
export function RecordToolbar(p: IRecordToolbarProps) {
  const { t } = useTranslation();
  const { record, editor, actions, isNew = false } = p;
  const counts = record.counts;
  const hasAssignee = p.rt.fields.some((f) => f.key === 'assignee' && f.type === 'user');
  const dirty = editor.dirtyKeys.length;
  const canComment = record.permissions?.comment !== false;
  const later = isNew ? t('recordPage.draft.saveFirst') : undefined;
  const observer = useIsObserver();
  const hiddenRequired = p.rt.fields.some((f) => f.required && isFieldRestricted(record, f)) ? t('recordPage.duplicateRestricted') : undefined;
  return (
    <div className="rec-bar" role="toolbar" aria-label={t('recordPage.toolbar')}>
      {p.canEdit && !editor.editing && (
        <button type="button" className="rec-btn rec-btn--primary" title={t('recordPage.editTitle')} aria-label={t('recordPage.editTitle')} onClick={editor.startEdit}>
          <RecordIcon name="edit" size={15} />{t('recordPage.edit')}
        </button>
      )}
      {p.canEdit && editor.editing && (
        <>
          <button type="button" className="rec-btn rec-btn--primary" title={t(isNew ? 'recordPage.draft.saveTitle' : 'recordPage.saveTitle')} aria-label={t(isNew ? 'recordPage.draft.saveTitle' : 'recordPage.saveTitle')}
            disabled={editor.saving} onClick={actions.save}>
            <RecordIcon name="check" size={15} strokeWidth={2} />{editor.saving ? t('recordPage.saving') : dirty ? t('recordPage.saveCount', { count: dirty }) : t('recordPage.save')}
          </button>
          <button type="button" className="rec-btn" title={t(isNew ? 'recordPage.draft.cancelTitle' : 'recordPage.cancelTitle')} aria-label={t(isNew ? 'recordPage.draft.cancelTitle' : 'recordPage.cancelTitle')}
            onClick={actions.cancel}>{t('recordPage.cancel')}</button>
        </>
      )}
      {p.canDelete && (
        <button type="button" className="rec-btn rec-btn--danger" title={later ?? t('recordPage.deleteTitle')} aria-label={t('recordPage.delete')} disabled={isNew} onClick={actions.remove}>
          <RecordIcon name="trash" size={15} />{t('recordPage.delete')}
        </button>
      )}
      {(p.canEdit || p.canDelete) && <span className="rec-bar__sep" aria-hidden />}
      {!observer && <FavouriteToggle recordId={record.id} disabledHint={later} />}
      <div className="rec-bar__icons">
        {hasAssignee && p.canEdit && <ToolIconButton glyph="assign" label={t('recordPage.assignMe')} onClick={actions.assignMe} />}
        {p.canEdit && <ToolIconButton glyph="copy" label={t('recordPage.duplicate')} hint={later ?? hiddenRequired} disabled={isNew || Boolean(hiddenRequired)} onClick={actions.duplicate} />}
        <ToolIconButton glyph="link" label={actions.linkCopied ? t('recordPage.linkCopied') : t('recordPage.copyLink')} hint={later} disabled={isNew} onClick={actions.copyLink} />
        <ToolIconButton glyph="print" label={t('recordPage.print')} onClick={actions.print} />
        {record.permissions?.export !== false && <ToolIconButton glyph="download" label={t('recordPage.export')} hint={later} disabled={isNew} onClick={actions.exportJson} />}
      </div>
      <CountPill glyph="link" count={counts?.links ?? 0} group={t('recordPage.links')} openLabel={t('recordPage.openLinks')} onOpen={() => p.onTab('related')}
        middle={<button type="button" className="rec-pill__mid" title={later ?? t('recordPage.navigator')} aria-label={t('recordPage.navigator')} disabled={isNew} onClick={p.onNavigator}><RecordIcon name="graph" /></button>}
        addLabel={t('recordPage.linkItem')} onAdd={p.onAddLink} canAdd={p.canEdit} addHint={later} />
      <CountPill glyph="comment" count={counts?.comments ?? 0} group={t('recordPage.commentsGroup')} openLabel={t('recordPage.openComments')} onOpen={() => p.onTab('comments')}
        addLabel={t('recordPage.writeComment')} onAdd={p.onAddComment} canAdd={canComment} addHint={later} />
      <CountPill glyph="clip" count={counts?.files ?? 0} group={t('recordPage.filesGroup')} openLabel={t('recordPage.openFiles')} onOpen={() => p.onTab('files')}
        addLabel={t('recordPage.attachFiles')} canAdd={canComment} addHint={later} add={(
          <label className="rec-pill__add" title={t('recordPage.attachFiles')}>+
            <input type="file" multiple hidden aria-label={t('recordPage.attachFiles')} onChange={(e) => { p.onAttach([...(e.target.files ?? [])]); e.target.value = ''; }} />
          </label>
        )} />
      <button type="button" className={cx('rec-btn', 'rec-btn--adv', p.panel === 'advisory' && 'is-on')} aria-pressed={p.panel === 'advisory'} title={later ?? t('recordPage.advisory')}
        aria-label={t('recordPage.advisory')} disabled={isNew} onClick={() => p.onPanel(p.panel === 'advisory' ? null : 'advisory')}>
        <RecordIcon name="bulb" size={15} />{t('recordPage.advisory')}
      </button>
      <button type="button" className={cx('rec-btn', 'rec-btn--map', p.panel === 'mapping' && 'is-on')} aria-pressed={p.panel === 'mapping'} title={later ?? t('recordPage.mapping')}
        aria-label={t('recordPage.mapping')} disabled={isNew} onClick={() => p.onPanel(p.panel === 'mapping' ? null : 'mapping')}>
        <RecordIcon name="link" size={15} />{t('recordPage.mapping')}
      </button>
      {!observer && (
        <button type="button" className="rec-btn rec-btn--ai" title={later ?? t('recordPage.askAiTitle')} aria-label={t('recordPage.askAiTitle')} disabled={isNew} onClick={() => actions.askAi()}>
          <RecordIcon name="sparkles" size={15} />{t('recordPage.askAi')}
        </button>
      )}
    </div>
  );
}
